import { useState, useMemo } from 'react';
import { Loader2, RefreshCw, ChevronRight, TrendingUp, TrendingDown, Minus, BarChart3, Rows3, Calendar } from 'lucide-react';
import { api } from '@halo/web-lib';
import type { DayCell, DayRunSummary } from './types';
import { RUN_TYPE_LABELS, TRIGGER_SOURCE_LABELS, STATUS_CONFIG, formatDuration, formatRows } from './types';
import { useNavigate } from 'react-router-dom';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const STATUS_CELL_STYLES: Record<DayCell['status'], { bg: string; text: string; hover: string }> = {
  completed: { bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900/50', text: 'text-green-800 dark:text-green-400', hover: 'hover:bg-green-100 dark:hover:bg-green-950/40 hover:border-green-300 dark:hover:border-green-900/50' },
  errored: { bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50', text: 'text-red-700 dark:text-red-400', hover: 'hover:bg-red-100 dark:hover:bg-red-950/40 hover:border-red-300 dark:hover:border-red-900/50' },
  running: { bg: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-900/50 animate-pulse', text: 'text-blue-700 dark:text-blue-400', hover: '' },
  partial: { bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50', text: 'text-amber-700 dark:text-amber-300', hover: 'hover:bg-amber-100 dark:hover:bg-amber-950/40 hover:border-amber-300 dark:hover:border-amber-900/50' },
  empty: { bg: 'bg-gray-50 dark:bg-gray-950 border-gray-200 dark:border-gray-800', text: 'text-gray-400 dark:text-gray-500', hover: 'hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-700' },
  future: { bg: 'bg-gray-50/50 border-gray-100 dark:border-gray-800', text: 'text-gray-300', hover: '' },
};

const STATUS_DOT: Record<DayCell['status'], string> = {
  completed: 'bg-green-500',
  errored: 'bg-red-400',
  running: 'bg-blue-400',
  partial: 'bg-amber-400',
  empty: 'bg-gray-300',
  future: 'bg-gray-200 dark:bg-gray-700',
};

interface Props {
  days: DayCell[];
  storeId: number;
  platform: string;
  configId: string;
  onRefresh: () => void;
}

/**
 * Calendar view for the current month's ingestion status.
 * Shows each day as a card with row count, run count, and status indicator.
 * Includes summary stats (total rows, daily avg, trend vs first/second half).
 */
export function BackfillCalendar({ days, storeId, platform, configId, onRefresh }: Props) {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const stats = useMemo(() => {
    const pastDays = days.filter((d) => d.status !== 'future');
    const completedDays = days.filter((d) => d.status === 'completed');
    const erroredDays = days.filter((d) => d.status === 'errored');
    const emptyDays = days.filter((d) => d.status === 'empty');
    const runningDays = days.filter((d) => d.status === 'running');

    const totalRows = pastDays.reduce((sum, d) => sum + (d.rowCount ?? (d.runs ?? []).reduce((s, r) => s + r.rowsIngested, 0)), 0);
    const totalRuns = pastDays.reduce((sum, d) => sum + (d.runs ?? []).length, 0);
    const avgRowsPerDay = completedDays.length > 0 ? Math.round(totalRows / completedDays.length) : 0;

    // Trend: compare first half vs second half of past days
    const mid = Math.floor(pastDays.length / 2);
    const firstHalf = pastDays.slice(0, mid);
    const secondHalf = pastDays.slice(mid);
    const firstHalfRows = firstHalf.reduce((sum, d) => sum + (d.rowCount ?? (d.runs ?? []).reduce((s, r) => s + r.rowsIngested, 0)), 0);
    const secondHalfRows = secondHalf.reduce((sum, d) => sum + (d.rowCount ?? (d.runs ?? []).reduce((s, r) => s + r.rowsIngested, 0)), 0);
    const firstAvg = firstHalf.length > 0 ? firstHalfRows / firstHalf.length : 0;
    const secondAvg = secondHalf.length > 0 ? secondHalfRows / secondHalf.length : 0;
    const trendPct = firstAvg > 0 ? Math.round(((secondAvg - firstAvg) / firstAvg) * 100) : 0;

    return {
      totalRows, totalRuns, avgRowsPerDay, trendPct,
      completed: completedDays.length,
      errored: erroredDays.length,
      empty: emptyDays.length,
      running: runningDays.length,
      future: days.filter((d) => d.status === 'future').length,
      coverage: pastDays.length > 0 ? Math.round((completedDays.length / pastDays.length) * 100) : 0,
    };
  }, [days]);

  // Fill all gaps: empty + failed + collected (unverified) days
  const gapDates = useMemo(
    () => days.filter((d) => d.status === 'empty' || d.status === 'errored' || d.status === 'partial').map((d) => d.date),
    [days],
  );

  if (days.length === 0) return null;

  const firstDate = days[0]!.date;
  const [yearStr, monthStr] = firstDate.split('-') as [string, string, string];
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const monthLabel = `${monthNames[month - 1]} ${year}`;

  const firstDow = (new Date(year, month - 1, 1).getDay() + 6) % 7;

  const cells: (DayCell | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (const d of days) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (DayCell | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const selectedDay = selectedDate ? days.find((d) => d.date === selectedDate) : null;
  const today = new Date().toISOString().slice(0, 10);

  async function handleBackfillDay(date: string) {
    setActing(`backfill-${date}`);
    try {
      await api.post('/api/v1/ingestion/admin/backfill', {
        storeId, platform, dates: [date], force: true,
      });
      onRefresh();
    } finally {
      setActing(null);
    }
  }

  async function handleBackfillEmpty() {
    const emptyDates = days.filter((d) => d.status === 'empty').map((d) => d.date);
    if (emptyDates.length === 0) return;
    setActing('backfill-empty');
    try {
      await api.post('/api/v1/ingestion/admin/backfill', {
        storeId, platform, dates: emptyDates, force: true,
      });
      onRefresh();
    } finally {
      setActing(null);
    }
  }

  async function handleBackfillMonth() {
    const month = `${yearStr}-${monthStr}`;
    setActing('backfill-month');
    try {
      await api.post('/api/v1/ingestion/admin/backfill', {
        storeId, platform, months: [month], force: true,
      });
      onRefresh();
    } finally {
      setActing(null);
    }
  }

  async function handleFillGaps() {
    if (gapDates.length === 0) return;
    setActing('fill-gaps');
    try {
      await api.post('/api/v1/ingestion/admin/backfill', {
        storeId, platform, dates: gapDates, force: true,
      });
      onRefresh();
    } finally {
      setActing(null);
    }
  }

  const TrendIcon = stats.trendPct > 5 ? TrendingUp : stats.trendPct < -5 ? TrendingDown : Minus;
  const trendColor = stats.trendPct > 5 ? 'text-green-600' : stats.trendPct < -5 ? 'text-red-500' : 'text-gray-400';

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-4 h-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900">{monthLabel}</h3>
          <span className="text-xs text-gray-400">{stats.coverage}% coverage</span>
        </div>
        <div className="flex items-center gap-2">
          {gapDates.length > 0 && (
            <button
              onClick={handleFillGaps}
              disabled={acting === 'fill-gaps'}
              className="px-2.5 py-1 text-xs font-medium border border-amber-300 dark:border-amber-900/50 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded-md hover:bg-amber-100 dark:hover:bg-amber-950/40 disabled:opacity-50 flex items-center gap-1.5"
            >
              {acting === 'fill-gaps' && <Loader2 className="w-3 h-3 animate-spin" />}
              Fill {gapDates.length} gaps
            </button>
          )}
          {stats.empty > 0 && (
            <button
              onClick={handleBackfillEmpty}
              disabled={acting === 'backfill-empty'}
              className="px-2.5 py-1 text-xs font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50 flex items-center gap-1.5"
            >
              {acting === 'backfill-empty' && <Loader2 className="w-3 h-3 animate-spin" />}
              Fill {stats.empty} missing
            </button>
          )}
          <button
            onClick={handleBackfillMonth}
            disabled={acting === 'backfill-month'}
            className="px-2.5 py-1 text-xs font-medium bg-gray-900 dark:bg-gray-950 text-white rounded-md hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50 flex items-center gap-1.5"
          >
            {acting === 'backfill-month' ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Backfill Month
          </button>
        </div>
      </div>

      {/* ── Stats Cards ── */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-white border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">
            <Rows3 className="w-3 h-3" /> This Month
          </div>
          <div className="text-base font-semibold text-gray-900">{formatRows(stats.totalRows)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mb-0.5">
            <BarChart3 className="w-3 h-3" /> Avg/Day
          </div>
          <div className="text-base font-semibold text-gray-900">{formatRows(stats.avgRowsPerDay)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mb-0.5">
            <TrendIcon className={`w-3 h-3 ${trendColor}`} /> Trend
          </div>
          <div className={`text-base font-semibold ${trendColor}`}>
            {stats.trendPct > 0 ? '+' : ''}{stats.trendPct}%
          </div>
        </div>
        <div className="bg-white border border-gray-200 dark:border-gray-800 rounded-lg px-3 py-2">
          <div className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5">Status</div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full bg-green-500" />{stats.completed}</span>
            {stats.errored > 0 && <span className="flex items-center gap-1 text-xs text-red-600"><span className="w-2 h-2 rounded-full bg-red-400" />{stats.errored}</span>}
            {stats.running > 0 && <span className="flex items-center gap-1 text-xs text-blue-600"><span className="w-2 h-2 rounded-full bg-blue-400" />{stats.running}</span>}
            <span className="flex items-center gap-1 text-xs text-gray-400"><span className="w-2 h-2 rounded-full bg-gray-300" />{stats.empty}</span>
          </div>
        </div>
      </div>

      {/* ── Calendar Grid ── */}
      <div className="select-none">
        {/* Day-of-week header */}
        <div className="grid grid-cols-7 gap-1.5 mb-1.5">
          {DAY_LABELS.map((label) => (
            <div key={label} className="text-[10px] text-gray-400 dark:text-gray-500 text-center font-medium py-1">{label}</div>
          ))}
        </div>

        {/* Week rows */}
        <div className="grid gap-1.5">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 gap-1.5">
              {week.map((cell, di) => {
                if (!cell) {
                  return <div key={`blank-${wi}-${di}`} className="h-14 rounded-lg" />;
                }
                const dayNum = parseInt(cell.date.split('-')[2]!, 10);
                const isSelected = selectedDate === cell.date;
                const isToday = cell.date === today;
                const isClickable = cell.status !== 'future';
                const totalRows = cell.rowCount ?? (cell.runs ?? []).reduce((sum, r) => sum + r.rowsIngested, 0);
                const style = STATUS_CELL_STYLES[cell.status];

                return (
                  <button
                    key={cell.date}
                    onClick={() => isClickable && setSelectedDate(isSelected ? null : cell.date)}
                    disabled={!isClickable}
                    className={`
                      h-14 rounded-lg border px-1.5 py-1 flex flex-col justify-between transition-all text-left
                      ${style.bg} ${style.hover}
                      ${cell.status === 'future' ? 'cursor-default' : 'cursor-pointer'}
                      ${isSelected ? 'ring-2 ring-gray-900 ring-offset-1' : ''}
                      ${isToday ? 'ring-2 ring-blue-500 ring-offset-1' : ''}
                    `}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${style.text}`}>{dayNum}</span>
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[cell.status]}`} />
                    </div>
                    {totalRows > 0 ? (
                      <div className="flex items-baseline justify-between">
                        <span className={`text-[11px] font-medium ${style.text}`}>{formatRows(totalRows)}</span>
                        {cell.attempts != null && cell.attempts > 1 && (
                          <span className="text-[9px] text-gray-400">{cell.attempts}x</span>
                        )}
                      </div>
                    ) : cell.status !== 'future' ? (
                      <span className="text-[9px] text-gray-300">—</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* ── Day Detail Panel ── */}
      {selectedDay && (
        <DayDetail
          day={selectedDay}
          storeId={storeId}
          configId={configId}
          acting={acting}
          onBackfill={() => handleBackfillDay(selectedDay.date)}
          onClose={() => setSelectedDate(null)}
          onNavigateRun={(runId) => navigate(`/ingestion/${storeId}/${configId}/runs/${runId}`)}
        />
      )}
    </div>
  );
}

/* ─── Day Detail Panel ──────────────────────────── */

function DayDetail({
  day,
  storeId,
  configId,
  acting,
  onBackfill,
  onClose,
  onNavigateRun,
}: {
  day: DayCell;
  storeId: number;
  configId: string;
  acting: string | null;
  onBackfill: () => void;
  onClose: () => void;
  onNavigateRun: (runId: string) => void;
}) {
  const dayLabel = new Date(day.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="mt-3 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-gray-800">{dayLabel}</span>
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
            day.status === 'completed' ? 'bg-green-100 text-green-700' :
            day.status === 'errored' ? 'bg-red-100 text-red-700' :
            day.status === 'running' ? 'bg-blue-100 text-blue-700' :
            day.status === 'partial' ? 'bg-amber-100 text-amber-700' :
            'bg-gray-100 text-gray-500'
          }`}>
            {day.status}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onBackfill}
            disabled={acting === `backfill-${day.date}`}
            className="px-2 py-0.5 text-[10px] font-medium border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded hover:bg-white dark:hover:bg-gray-900 disabled:opacity-50 flex items-center gap-1"
          >
            {acting === `backfill-${day.date}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Re-backfill
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xs">✕</button>
        </div>
      </div>

      {!day.runs || day.runs.length === 0 ? (
        <div className="text-[10px] text-gray-500 dark:text-gray-400 space-y-1">
          {day.rowCount != null && day.rowCount > 0 && (
            <p><span className="text-gray-700 font-medium">{day.rowCount.toLocaleString()}</span> rows in ClickHouse</p>
          )}
          {day.attempts != null && day.attempts > 0 && (
            <p>{day.attempts} attempt{day.attempts !== 1 ? 's' : ''}</p>
          )}
          {day.lastError && (
            <p className="text-red-500 truncate max-w-xs" title={day.lastError}>{day.lastError}</p>
          )}
          {(!day.rowCount || day.rowCount === 0) && !day.lastError && (
            <p className="text-gray-400">No data for this day.</p>
          )}
        </div>
      ) : day.runs && day.runs.length > 0 ? (
        <div className="divide-y divide-gray-200 -mx-1">
          {day.runs.map((run) => (
            <RunRow key={run.id} run={run} onNavigate={() => onNavigateRun(run.id)} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ─── Run Row ───────────────────────────────────── */

function RunRow({ run, onNavigate }: { run: DayRunSummary; onNavigate: () => void }) {
  const rs = STATUS_CONFIG[run.status] ?? STATUS_CONFIG['completed']!;
  const RunIcon = rs.icon;

  const duration = run.startedAt && run.completedAt
    ? formatDuration(new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime())
    : null;

  return (
    <div
      onClick={onNavigate}
      className="flex items-center justify-between px-1 py-1.5 cursor-pointer hover:bg-white dark:hover:bg-gray-900 rounded transition-colors"
    >
      <div className="flex items-center gap-2 min-w-0">
        <RunIcon className={`w-3 h-3 shrink-0 ${rs.className.split(' ')[0]}`} />
        <div className="min-w-0">
          <div className="text-[10px] text-gray-800">
            {RUN_TYPE_LABELS[run.runType] ?? run.runType}
            {(() => {
              const ts = TRIGGER_SOURCE_LABELS[run.triggerSource];
              return ts ? (
                <span className={`ml-1.5 px-1 py-0.5 rounded text-[8px] font-medium ${ts.className}`}>{ts.label}</span>
              ) : null;
            })()}
            <span className="text-gray-400 ml-1">
              {run.windowStart === run.windowEnd
                ? run.windowStart
                : `${run.windowStart} → ${run.windowEnd}`}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[9px] text-gray-400">
            <span>{new Date(run.startedAt).toLocaleString()}</span>
            {duration && <span>{duration}</span>}
            {run.rowsIngested > 0 && <span>{run.rowsIngested.toLocaleString()} rows</span>}
          </div>
          {run.errorMessage && (
            <div className="text-[9px] text-red-500 truncate max-w-[300px]">{run.errorMessage}</div>
          )}
        </div>
      </div>
      <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />
    </div>
  );
}
